import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from './useAuth';
import { useUser } from '../Context/userContext';
import { apiFetch, apiJson } from '../lib/api';
import type { XactoMatrixData, XactoGroupBy } from './useXactoMatrix';
import { toUserMessage } from '../lib/apiError';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AiAnalysisState =
  | 'idle'
  | 'preparing'
  | 'streaming'
  | 'completed'
  | 'stopped'
  | 'error';

export type AiPivotGrouping = 'merchant-month' | 'merchant-year' | 'category-month' | 'category-year';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: Date;
}

export interface AgentOption {
  id: string;
  label: string;
  defaultPrompt: string;
}

export interface AiPublicConfig {
  defaultModel: string;
  defaultAgent: string;
  agents: AgentOption[];
}

interface StreamChunk {
  type: 'delta' | 'done' | 'error' | 'usage';
  content?: string;
  inputTokens?: number;
  outputTokens?: number;
  error?: string;
}

export interface TokenUsage {
  input: number;
  output: number;
}

// ─── History types ────────────────────────────────────────────────────────────

export interface HistoryConversation {
  id: string;
  title: string | null;
  groupBy: string;
  agentId: string;
  model: string;
  dateRange: string | null;
  createdAt: string;
  _count: { messages: number };
  usageLogs: { inputTokens: number; outputTokens: number; costEstimate: number }[];
}

export interface HistoryConversationDetail {
  id: string;
  title: string | null;
  groupBy: string;
  agentId: string;
  model: string;
  dateRange: string | null;
  createdAt: string;
  messages: { id: string; role: string; content: string; createdAt: string; inputTokens?: number; outputTokens?: number }[];
  usageLogs: { inputTokens: number; outputTokens: number; costEstimate: number }[];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseAiAnalysisReturn {
  // State
  analysisState: AiAnalysisState;
  messages: ChatMessage[];
  error: string | null;
  tokenUsage: TokenUsage;

  // Config
  config: AiPublicConfig | null;
  configError: string | null;
  configLoading: boolean;
  models: string[];
  selectedAgent: string;
  selectedModel: string;
  prompt: string;

  // History
  history: HistoryConversation[];
  historyTotal: number;
  historyPage: number;
  historyLoading: boolean;
  historyDetail: HistoryConversationDetail | null;
  historyDetailLoading: boolean;
  loadHistory: (groupBy?: string, page?: number) => Promise<void>;
  loadHistoryDetail: (id: string) => Promise<void>;
  clearHistoryDetail: () => void;
  resumeConversation: (conv: HistoryConversationDetail) => void;

  // Actions
  setSelectedAgent: (id: string) => void;
  setSelectedModel: (model: string) => void;
  setPrompt: (p: string) => void;
  resetPrompt: () => void;
  analyze: (matrixData: XactoMatrixData, groupBy: XactoGroupBy) => Promise<void>;
  sendFollowUp: (followUpText: string, matrixData: XactoMatrixData, groupBy: XactoGroupBy) => Promise<void>;
  analyzePeriod: (period: { start: string; end: string }, grouping: AiPivotGrouping) => Promise<void>;
  stop: () => void;
  reset: () => void;
  loadConfig: (force?: boolean) => Promise<void>;
}

function makeId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function useAiAnalysis(): UseAiAnalysisReturn {
  const { session } = useAuth();
  const { selectedTenant } = useUser();
  const token = session?.access_token;
  const tenantId = selectedTenant?.id;

  const [analysisState, setAnalysisState] = useState<AiAnalysisState>('idle');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tokenUsage, setTokenUsage] = useState<TokenUsage>({ input: 0, output: 0 });

  const [config, setConfig] = useState<AiPublicConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [models, setModels] = useState<string[]>([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [prompt, setPrompt] = useState('');

  const abortRef = useRef<AbortController | null>(null);
  const stoppedRef = useRef(false);

  // ─── History state ───────────────────────────────────────────────────────────
  const [history, setHistory] = useState<HistoryConversation[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDetail, setHistoryDetail] = useState<HistoryConversationDetail | null>(null);
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);

  // ─── Config loading ──────────────────────────────────────────────────────────

  const loadConfig = useCallback(async (force = false) => {
    if ((config && !force) || !token) return;
    setConfigLoading(true);
    setConfigError(null);
    try {
      const [cfg, modelList] = await Promise.all([
        apiJson<AiPublicConfig>('/api/ai/config', undefined, token),
        apiJson<{ models: string[] }>('/api/ai/models', undefined, token),
      ]);
      setConfig(cfg);
      setModels(modelList.models);
      setSelectedAgent(prev => prev || cfg.defaultAgent);
      setSelectedModel(prev => prev || cfg.defaultModel);
      setConfigError(null);
    } catch (err) {
      const msg = toUserMessage(err, "We couldn't load the AI configuration. Please try again.");
      setConfigError(msg);
      console.error('AI config load failed:', err);
    } finally {
      setConfigLoading(false);
    }
  }, [config, token]);

  const resetPrompt = useCallback(() => {
    if (!config) return;
    const agent = config.agents.find(a => a.id === selectedAgent) ?? config.agents[0];
    setPrompt(agent?.defaultPrompt ?? '');
  }, [config, selectedAgent]);

  useEffect(() => {
    if (config && selectedAgent) {
      const agent = config.agents.find(a => a.id === selectedAgent);
      if (agent) setPrompt(agent.defaultPrompt);
    }
  }, [selectedAgent, config]);

  // ─── Controls ────────────────────────────────────────────────────────────────

  const stop = useCallback(() => {
    stoppedRef.current = true;
    abortRef.current?.abort();
    setAnalysisState('stopped');
  }, []);

  /** Full reset: clears messages and all state. Used by "New query". */
  const reset = useCallback(() => {
    stoppedRef.current = false;
    abortRef.current = null;
    setMessages([]);
    setError(null);
    setTokenUsage({ input: 0, output: 0 });
    setAnalysisState('idle');
  }, []);

  // ─── SSE stream parser ───────────────────────────────────────────────────────

  async function drainStream(
    response: Response,
    assistantMsgId: string,
    onDone: (inputTokens: number, outputTokens: number) => void,
  ): Promise<'completed' | 'stopped' | 'error'> {
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let inputTokens = 0;
    let outputTokens = 0;

    try {
      while (true) {
        if (stoppedRef.current) { reader.cancel(); return 'stopped'; }

        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;

          try {
            const chunk: StreamChunk = JSON.parse(raw);

            if (chunk.type === 'delta' && chunk.content) {
              // Functional update — safe during rapid streaming
              setMessages(prev => prev.map(m =>
                m.id === assistantMsgId
                  ? { ...m, content: m.content + chunk.content! }
                  : m
              ));
            } else if (chunk.type === 'usage') {
              inputTokens = chunk.inputTokens ?? 0;
              outputTokens = chunk.outputTokens ?? 0;
              setTokenUsage({ input: inputTokens, output: outputTokens });
            } else if (chunk.type === 'error') {
              setError(chunk.error ?? 'Analysis error');
              setAnalysisState('error');
              return 'error';
            } else if (chunk.type === 'done') {
              onDone(inputTokens, outputTokens);
              return 'completed';
            }
          } catch {
            // Malformed SSE line — skip
          }
        }
      }
    } finally {
      reader.cancel().catch(() => {});
    }

    onDone(inputTokens, outputTokens);
    return 'completed';
  }

  // ─── analyze (matrix flow) ───────────────────────────────────────────────────

  const normalizeGroupBy = (g: XactoGroupBy): 'merchant' | 'category' =>
    g === 'category' ? 'category' : 'merchant';

  const analyze = useCallback(async (matrixData: XactoMatrixData, groupBy: XactoGroupBy) => {
    if (!token) { setError('No auth token'); return; }
    if (!tenantId) { setError('No tenant selected'); return; }

    // Partial reset — preserve existing messages, start fresh analysis state
    stoppedRef.current = false;
    abortRef.current = null;
    setError(null);
    setTokenUsage({ input: 0, output: 0 });
    setAnalysisState('preparing');

    const userPromptCopy = prompt.trim();
    const userMsgId = makeId();
    const assistantMsgId = makeId();

    setMessages(prev => [
      ...prev,
      { id: userMsgId, role: 'user', content: userPromptCopy || '(default prompt)', createdAt: new Date() },
      { id: assistantMsgId, role: 'assistant', content: '', createdAt: new Date() },
    ]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await apiFetch(
        '/api/ai/analyze-finances',
        {
          method: 'POST',
          headers: {
            Accept: 'text/event-stream',
            'x-tenant-id': tenantId,
          },
          body: JSON.stringify({
            groupBy: normalizeGroupBy(groupBy),
            matrixData,
            agentId: selectedAgent || undefined,
            model: selectedModel || undefined,
            userPrompt: userPromptCopy || undefined,
          }),
          signal: controller.signal,
        },
        token,
      );

      if (!resp.ok) {
        const errText = await resp.text().catch(() => `HTTP ${resp.status}`);
        throw new Error(errText);
      }

      setAnalysisState('streaming');

      const result = await drainStream(resp, assistantMsgId, () => {});
      if (!stoppedRef.current && result === 'completed') setAnalysisState('completed');
    } catch (err) {
      if (stoppedRef.current) return;
      const msg = toUserMessage(err, "We couldn't reach the server. Please check your connection and try again.");
      setError(msg);
      setAnalysisState('error');
    }
  }, [token, tenantId, selectedAgent, selectedModel, prompt]);

  // ─── sendFollowUp (continue an existing conversation) ────────────────────────

  const sendFollowUp = useCallback(async (
    followUpText: string,
    matrixData: XactoMatrixData,
    groupBy: XactoGroupBy,
  ) => {
    if (!token) { setError('No auth token'); return; }
    if (!tenantId) { setError('No tenant selected'); return; }
    if (!followUpText.trim()) return;

    // Capture current messages as history before appending the new turn
    const historyForBackend = messages
      .filter(m => m.content.length > 0)
      .map(m => ({ role: m.role, content: m.content }));

    stoppedRef.current = false;
    abortRef.current = null;
    setError(null);
    setTokenUsage({ input: 0, output: 0 });
    setAnalysisState('preparing');

    const userMsgId = makeId();
    const assistantMsgId = makeId();

    setMessages(prev => [
      ...prev,
      { id: userMsgId, role: 'user', content: followUpText, createdAt: new Date() },
      { id: assistantMsgId, role: 'assistant', content: '', createdAt: new Date() },
    ]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await apiFetch(
        '/api/ai/analyze-finances',
        {
          method: 'POST',
          headers: {
            Accept: 'text/event-stream',
            'x-tenant-id': tenantId,
          },
          body: JSON.stringify({
            groupBy: normalizeGroupBy(groupBy),
            matrixData,
            agentId: selectedAgent || undefined,
            model: selectedModel || undefined,
            userPrompt: followUpText,
            conversationHistory: historyForBackend.length > 0 ? historyForBackend : undefined,
          }),
          signal: controller.signal,
        },
        token,
      );

      if (!resp.ok) {
        const errText = await resp.text().catch(() => `HTTP ${resp.status}`);
        throw new Error(errText);
      }

      setAnalysisState('streaming');

      const result = await drainStream(resp, assistantMsgId, () => {});
      if (!stoppedRef.current && result === 'completed') setAnalysisState('completed');
    } catch (err) {
      if (stoppedRef.current) return;
      const msg = toUserMessage(err, "We couldn't reach the server. Please check your connection and try again.");
      setError(msg);
      setAnalysisState('error');
    }
  }, [token, tenantId, selectedAgent, selectedModel, messages]);

  // ─── analyzePeriod (period/transaction flow) ─────────────────────────────────

  const analyzePeriod = useCallback(async (
    period: { start: string; end: string },
    grouping: AiPivotGrouping
  ) => {
    if (!token) { setError('No auth token'); return; }
    if (!tenantId) { setError('No tenant selected'); return; }

    stoppedRef.current = false;
    abortRef.current = null;
    setError(null);
    setTokenUsage({ input: 0, output: 0 });
    setAnalysisState('preparing');

    const userPromptCopy = prompt.trim();
    const userMsgId = makeId();
    const assistantMsgId = makeId();

    setMessages(prev => [
      ...prev,
      { id: userMsgId, role: 'user', content: userPromptCopy || '(default prompt)', createdAt: new Date() },
      { id: assistantMsgId, role: 'assistant', content: '', createdAt: new Date() },
    ]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await apiFetch(
        '/api/ai/analysis',
        {
          method: 'POST',
          headers: {
            Accept: 'text/event-stream',
            'x-tenant-id': tenantId,
          },
          body: JSON.stringify({
            viewMode: 'pivot',
            grouping,
            period,
            agentId: selectedAgent || undefined,
            model: selectedModel || undefined,
            userPrompt: userPromptCopy || undefined,
          }),
          signal: controller.signal,
        },
        token,
      );

      if (!resp.ok) {
        const errText = await resp.text().catch(() => `HTTP ${resp.status}`);
        throw new Error(errText);
      }

      setAnalysisState('streaming');

      const result = await drainStream(resp, assistantMsgId, () => {});
      if (!stoppedRef.current && result === 'completed') setAnalysisState('completed');
    } catch (err) {
      if (stoppedRef.current) return;
      const msg = toUserMessage(err, "We couldn't reach the server. Please check your connection and try again.");
      setError(msg);
      setAnalysisState('error');
    }
  }, [token, tenantId, selectedAgent, selectedModel, prompt]);

  // ─── History actions ─────────────────────────────────────────────────────────

  const loadHistory = useCallback(async (groupBy?: string, page = 1) => {
    if (!token || !tenantId) return;
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '20' });
      if (groupBy) params.set('groupBy', groupBy);
      const data = await apiJson<{ conversations: HistoryConversation[]; total: number; page: number }>(
        `/api/ai/conversations?${params}`,
        { headers: { 'x-tenant-id': tenantId } },
        token,
      );
      setHistory(data.conversations);
      setHistoryTotal(data.total);
      setHistoryPage(data.page);
    } catch (err) {
      console.error('Failed to load conversation history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [token, tenantId]);

  const loadHistoryDetail = useCallback(async (id: string) => {
    if (!token || !tenantId) return;
    setHistoryDetailLoading(true);
    try {
      const data = await apiJson<HistoryConversationDetail>(
        `/api/ai/conversations/${id}`,
        { headers: { 'x-tenant-id': tenantId } },
        token,
      );
      setHistoryDetail(data);
    } catch (err) {
      console.error('Failed to load conversation detail:', err);
    } finally {
      setHistoryDetailLoading(false);
    }
  }, [token]);

  const clearHistoryDetail = useCallback(() => {
    setHistoryDetail(null);
  }, []);

  /**
   * Load a past conversation's messages into the chat and switch to idle
   * so the user can continue the conversation with the current matrix data.
   */
  const resumeConversation = useCallback((conv: HistoryConversationDetail) => {
    stoppedRef.current = false;
    abortRef.current = null;
    const loaded: ChatMessage[] = conv.messages.map(m => ({
      id: m.id,
      role: m.role as 'user' | 'assistant',
      content: m.content,
      createdAt: new Date(m.createdAt),
    }));
    setMessages(loaded);
    setError(null);
    setTokenUsage({ input: 0, output: 0 });
    setAnalysisState('idle');
    setHistoryDetail(null);
  }, []);

  return {
    analysisState,
    messages,
    error,
    tokenUsage,
    config,
    configError,
    configLoading,
    models,
    selectedAgent,
    selectedModel,
    prompt,
    history,
    historyTotal,
    historyPage,
    historyLoading,
    historyDetail,
    historyDetailLoading,
    loadHistory,
    loadHistoryDetail,
    clearHistoryDetail,
    resumeConversation,
    setSelectedAgent,
    setSelectedModel,
    setPrompt,
    resetPrompt,
    analyze,
    sendFollowUp,
    analyzePeriod,
    stop,
    reset,
    loadConfig,
  };
}
